import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { permissionKeysToJson } from '@/modules/settings/permissions/catalog/permissionCatalog';
import { RoleFormBlock } from './components/RoleFormBlock';
import { useRolesNew } from './hooks/useRolesNew';
import type { RoleFormData, RoleTemplate } from './types/rolesNew.types';

const initialForm: RoleFormData = {
  code: '',
  name: '',
  description: '',
  permissionsJson: {},
};

export function RolesNewPage() {
  const navigate = useNavigate();
  const { availableModules, templates, isLoading, isSubmitting, createRole, generateCode } = useRolesNew();
  const [formData, setFormData] = useState<RoleFormData>(initialForm);

  useEffect(() => {
    if (!formData.name.trim()) {
      setFormData((current) => ({ ...current, code: '' }));
      return;
    }

    const timer = window.setTimeout(async () => {
      const code = await generateCode(formData.name);
      setFormData((current) => ({ ...current, code }));
    }, 250);

    return () => window.clearTimeout(timer);
  }, [formData.name, generateCode]);

  const applyTemplate = (template: RoleTemplate) => {
    setFormData({
      code: '',
      name: template.name,
      description: template.description,
      permissionsJson: permissionKeysToJson(template.permissionKeys),
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const success = await createRole(formData);
    if (success) navigate('?tab=roles');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex flex-col gap-3 rounded-md border border-slate-100 bg-white p-3 shadow-sm md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('?tab=roles')}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-100 bg-white text-slate-600"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="space-y-2 text-sm">
            <h1 className="text-lg font-medium text-slate-900">Thêm vai trò mới</h1>
            <p className="text-slate-500">Thiết lập RBAC, quyền rủi ro và phạm vi truy cập.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate('?tab=roles')}
            className="h-10 flex-1 rounded-md border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 md:flex-none"
          >
            Hủy
          </button>
          <button
            type="submit"
            disabled={isSubmitting || isLoading}
            className="h-10 flex-1 rounded-md bg-emerald-600 px-4 text-sm font-semibold text-white disabled:opacity-50 md:flex-none"
          >
            {isSubmitting ? 'Đang lưu' : 'Lưu vai trò'}
          </button>
        </div>
      </div>

      <RoleFormBlock
        formData={formData}
        templates={templates}
        availableModules={availableModules}
        isLoading={isLoading}
        onChange={setFormData}
        onNameChange={(name) => setFormData((current) => ({ ...current, name }))}
        onTemplateApply={applyTemplate}
      />
    </form>
  );
}
